import React, { useEffect, useMemo, useState } from 'react';
import { DeskUsage, getOfficeDeskUsage, toExcelDownload } from '@features/reporting/services/ReportingService';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { Moment } from "moment";
import { OfficeUsageContentWrapper } from "./OfficeUsageStructure";
import styled from "styled-components";
import { naturalSort } from "@shared/utils/ArrayUtils";
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import { HeadlineMedium, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { ProgressionLine } from '@shared/ui/atoms/ProgressionLine';
import { hexToRGBA } from "@shared/utils/RandomColour";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';

function getColour(percentage: number): string {
  if (percentage > 0.5) {
    return Colours.green;
  } else if (percentage > 0.3) {
    return Colours.yellow;
  } else if (percentage > 0.1) {
    return Colours.orange;
  } else {
    return Colours.red;
  }
}

export function OfficeDeskUsage(props: Props) {
  const {fromDate, selectedOffice, toDate} = props;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DeskUsage[]>([]);
  const {t} = useTranslation();

  useEffect(() => {
    if (selectedOffice && fromDate) {
      setLoading(true);
      getOfficeDeskUsage(selectedOffice, fromDate, toDate)
        .then(data => setData(data))
        .finally(() => setLoading(false));
    }
  }, [fromDate, selectedOffice, toDate]);

  const top10: DeskUsage[] = useMemo(() => {
    return (naturalSort([...data], 'usage') as DeskUsage[]).reverse().filter(v => v.usage > 0).slice(0, 10)
  }, [data]);

  const bottom10: DeskUsage[] = useMemo(() => {
    return (naturalSort([...data], 'usage') as DeskUsage[]).slice(0, 10)
  }, [data]);

  const onDownloadClicked = () => {
    const rows = (naturalSort([...data], 'usage') as DeskUsage[]).reverse().map(row => ({
      desk: row.label,
      daysUsed: row.usage,
      daysInRange: row.daysInRange,
      usagePercentage: row.daysInRange > 0 ? Math.round((row.usage / row.daysInRange) * 100) : 0,
    }));
    const dateRange = toDate ? `${fromDate?.format('D MMM YYYY')} - ${toDate.format('D MMM YYYY')}` : fromDate?.format('MMM YYYY');
    toExcelDownload(rows, `${selectedOffice?.label} desk usage ${dateRange}`);
  }

  return (
    <OfficeUsageContentWrapper>
      <TitleRow>
        <HeadlineMedium>{t('reports-page.office-usage.deskUsage')}</HeadlineMedium>
        <OutlineButton click={onDownloadClicked}
                       disabled={loading || data.length === 0}
                       text={'reports-page.office-usage.download-desk-usage-btn'} />
      </TitleRow>
      {loading ? <Center>
        <LoadingSpinner hideBorder={true} hideText={true} />
      </Center> : <>
        <Row>
          <Column>
            <HeadlineSmall>{t('reports-page.office-usage.top10Desks')}</HeadlineSmall>

            {top10.map((row, key) => (<React.Fragment key={key}>
              <ProgressionLine fgColour={getColour(row.usage / row.daysInRange)}
                               showPillSummary={true}
                               bgColour={hexToRGBA(getColour(row.usage / row.daysInRange), 0.1)}
                               title={row.label}
                               percentage={row.usage / row.daysInRange} />

            </React.Fragment>))}
          </Column>
          <Column>
            <HeadlineSmall>{t('reports-page.office-usage.bottom10Desks')}</HeadlineSmall>

            {bottom10.map((row, key) => (<React.Fragment key={key}>
              <ProgressionLine fgColour={getColour(row.usage / row.daysInRange)}
                               showPillSummary={true}
                               bgColour={hexToRGBA(getColour(row.usage / row.daysInRange), 0.1)}
                               title={row.label}
                               percentage={row.usage / row.daysInRange} />

            </React.Fragment>))}
          </Column>
        </Row>
      </>}
    </OfficeUsageContentWrapper>
  )
}

interface Props {
  selectedOffice?: OfficeEntity;
  fromDate?: Moment;
  toDate?: Moment;
}

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`

const Row = styled.div`
  display: flex;
  width: 100%;
  .progressionLine {
    padding: 0;
  }
  .progressionLine__pill {
    width: 182px;
  }
  h3 {
    margin-bottom: 24px;
  }
  flex-direction: column;
  @media (${tablet}) {
    flex-direction: row;
  }
`

const Column = styled.div`
  width: 100%;
  @media (${tablet}) {
    width: 50%;
  }
`
